import Cookies from 'js-cookie';
import get from 'lodash/get';
import omit from 'lodash/omit';

import {
  initSessionId,
  JA_SESSION_ID_COOKIE_NAME,
} from '@jerry-serverless/ui-analytics/lib/scripts/jerry-analytics-script';
import { md5 } from '@jerry-serverless/crypto';

import type { AppData } from '../../types/app_data';

/**
 * Set landing event to PodScribe Pixel.
 *
 * @param user
 */
export const setLandingForPodScribePixel = async (sessionID?: string): Promise<void> => {
  // @ts-ignore
  if (window.podscribe) {
    const initSessionID: string | undefined = initSessionId();
    // @ts-ignore
    window.podscribe('view', {
      device_id: sessionID || initSessionID,
      session_id: sessionID || initSessionID,
    });
  }
};

/**
 * Generate metadata for PodScribe Pixel events.
 * @param appData
 * @returns
 */
const generateMetadata = (appData?: AppData): Record<string, unknown> => {
  return {
    device_id: appData?.sessionId,
    order_number: appData?.id,
    hashed_email: appData?.email ? md5(appData.email) : undefined,

    session_id: appData?.sessionId,
    id: appData?.id,
    mlCoverageEnabled: appData?.mlCoverageEnabled,
    isOnboardingLite: appData?.isOnboardingLite,
    enableLDU: appData?.enableLDU,
  };
};

/**
 * Track signup conversion.
 */
export const trackSignedUpForPodScribe = (appData?: AppData): void => {
  // // @ts-ignore
  // if (window.podscribe) {
  //   // @ts-ignore
  //   window.podscribe('signup', generateMetadata(appData));
  // }
  setPendingPodScribeEvents('signup', appData);
};

/**
 * Track onboarded conversion.
 */
export const trackOnboardedForPodScribe = (appData?: AppData): void => {
  setPendingPodScribeEvents('purchase', appData);
};

const pendingEventKeyPrefix = 'pendingPodScribeEvents';
const pendingInterval = 60 * 1000; // 60 seconds

/**
 * Localstorage for pending PodScribe Pixel events. Used for trackSignedUpForPodScribe, trackOnboardedForPodScribe,
 * because the browser will reload when these two events are triggered, and we need to ensure that the events
 * are sent after the reload.
 * @param appData
 */
const setPendingPodScribeEvents = (podScribeEvent: string, appData?: AppData): void => {
  if (!appData?.sessionId) {
    throw new Error('appData.session_id is required to store pending PodScribe events.');
  }
  const key = `${pendingEventKeyPrefix}_${appData.sessionId}`;
  const now = Date.now();
  const pendingEvents = JSON.parse(localStorage.getItem(key) || '[]');
  const metadata = generateMetadata(appData);
  pendingEvents.push({ ...metadata, _pendingTime: now, _podScribeEvent: podScribeEvent });
  localStorage.setItem(key, JSON.stringify(pendingEvents));
};

/**
 * Get pending PodScribe Pixel events from localstorage.
 * @param sessionId
 * @returns {Record<string, unknown>[]}
 */
const getPendingPodScribeEvents = (sessionId?: string): Record<string, unknown>[] => {
  if (!sessionId) {
    return [];
  }
  const key = `${pendingEventKeyPrefix}_${sessionId}`;
  const items = JSON.parse(localStorage.getItem(key) || '[]');
  const oneMinuteAgo = Date.now() - pendingInterval;
  const filtered =
    items.filter((item: { _pendingTime?: number }) => item._pendingTime && item._pendingTime >= oneMinuteAgo) || [];
  // Filter out expired items and reset key value
  localStorage.setItem(key, JSON.stringify(filtered));
  return filtered;
};

/**
 * Resend pending PodScribe Pixel events.
 */
export const resendPendingPodScribeEvents = (cb?: (action: string) => void): void => {
  const sessionID = Cookies.get(JA_SESSION_ID_COOKIE_NAME) || window[JA_SESSION_ID_COOKIE_NAME];
  const pendingPodScribeEvents = getPendingPodScribeEvents(sessionID);
  if (pendingPodScribeEvents?.length > 0) {
    pendingPodScribeEvents.forEach((eventData) => {
      // @ts-ignore
      if (window.podscribe) {
        const podScribeEvent = get(eventData, '_podScribeEvent') as string;
        // @ts-ignore
        window.podscribe(podScribeEvent, omit(eventData, ['_pendingTime', '_podScribeEvent']));
        cb && cb(podScribeEvent);
      }
    });
  }
};
